import MoreVertIcon from "@mui/icons-material/MoreVert";
import NotInterestedIcon from "@mui/icons-material/NotInterested";
import {
    Alert,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import { withStyles } from "@mui/styles";
import axios from "axios";
import * as React from "react";
import { LoadingWrapper } from "../../components/LoadingWrapper";
import {Config} from "../../config/config";
import { Helpers } from "../../utils/helpers";
import { PopupMessage } from "../../utils/message";
import { EditPack } from "./edit_pack";
import { NewPack } from "./new_pack";

interface BountyPacksState {
    packs: ChestData[];
    loaded: boolean;
    anchorEl: any;
    menuVisible: boolean;
    activeSimulationItem: ChestData | null;
    simulateDialogVisible: boolean;
}

interface BountyPacksProps {
    activeUser: any;
    classes: any;

}

export interface ChestData {
    drive_key: string;
    pack_name: string;
    graphic_id: string;
    description: string;
    currency: string;
    price: number;
    pack_id: string;
    created_at: string;
    updated_at: string;
    localization_title: string;
    localization_desc: string;
    rarity: string;
}

class BountyPacksWrapped extends React.Component<BountyPacksProps, BountyPacksState> {

    constructor(props) {
        super(props);
        // List with grid items
        this.state = {
            packs: [],
            loaded: false,
            anchorEl: null,
            menuVisible: false,
            // Simulation
            activeSimulationItem: null,
            simulateDialogVisible: false,
        };

        document.title = Helpers.documentTitle("Bounty Packs");
    }

    componentDidMount() {
        this.fetchBountyPacks();
    }

    refreshList = () => this.setState({ loaded: false }, () => {
        this.fetchBountyPacks();
    })

    openRowMenu = (event) => this.setState({ anchorEl: event.currentTarget }, () => this.setState({ menuVisible: true }));
    closeRowMenu = () => this.setState({ menuVisible: false });
    clearMenuAnchor = () => this.setState({ anchorEl: null });

    fetchBountyPacks = async () => {
        try {
            // Put data to S3
            const response = await axios.get(`${Config.getInstance.apiUrl}/api/bounty/packs`, {
                headers: {
                    Authorization: Helpers.getSessionId(),
                },
            });

            this.setState({ packs: response.data.data, loaded: true });
        } catch (error) {
            this.setState({ loaded: true });
            PopupMessage.error("Something went wrong");
        }
    }

    getFileActions = () => {
        if (this.state.anchorEl) {
            const chestId = this.state.anchorEl.getAttribute("chest-key");
            const packObject = this.state.packs.find(chest => chest.pack_id === chestId);

            if (packObject) {
                 return [
                    <EditPack
                        chest={packObject}
                        refreshParent={this.refreshList}
                        closeRowMenu={this.closeRowMenu}
                        key="edit-chest-key"
                    />,
                    <MenuItem onClick={() => this.viewSheets(null, packObject.drive_key)} key="sheets-chest-key">
                        Google sheet
                    </MenuItem>,
                ];
            }
        }

        return;
    }

    viewSheets = (event, driveKey) => {
        if (!event || event._dispatchListeners.length === 1) {
            const win = window.open(`https://docs.google.com/spreadsheets/d/${driveKey}/edit`, "_blank");
            if (win) {
                win.focus();
            }
        }
    }

    formatPacksToTable = (packs) => {
        if (!this.state.loaded) {
            return <TableRow key="loading-row">
                <TableCell colSpan={4}>
                    {Helpers.renderSkeletonRows(5, 50)}
                </TableCell>
            </TableRow>
        }

        if (packs.length === 0) {
            return <TableRow key="records_not_found">
                <TableCell colSpan={4}>
                    <Alert severity="info">No bounty packs</Alert>
                </TableCell>
            </TableRow>
        }

        return packs.map(chest => (
            <TableRow key={chest.pack_id} hover={true}>
                <TableCell>{chest.pack_id}</TableCell>
                <TableCell>{chest.pack_name}</TableCell>
                <TableCell>{Helpers.formatDateWithTime(chest.updated_at)}</TableCell>
                <TableCell style={{ textAlign: "right" }}>
                    <IconButton
                        aria-label="More"
                        aria-haspopup="true"
                        onClick={this.openRowMenu}
                        chest-key={chest.pack_id}
                    >
                        <MoreVertIcon />
                    </IconButton>
                </TableCell>
            </TableRow>
        ));
    }

    formatTableBody(packs) {
        return <React.Fragment>
            <Table className="min-table-size">
                <TableHead>
                    <TableRow>
                        <TableCell style={{ width: 20 }}>Id</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell style={{ width: 200 }}>Last modified</TableCell>
                        <TableCell style={{ width: 100 }}></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {this.formatPacksToTable(packs)}
                </TableBody>
            </Table>
            <Menu
                id="long-menu"
                anchorEl={this.state.anchorEl}
                open={this.state.menuVisible}
                onClose={this.closeRowMenu}
                TransitionProps={{
                    onExited: this.clearMenuAnchor
                }}
            >
                {this.getFileActions()}
            </Menu>
        </React.Fragment>;

    }

    render() {
        const { classes } = this.props;

        return <div className="contentPadding">
            <Paper className="root" elevation={0}>
                <div style={{ float: "left", padding: "15px 22px 15px 22px", width: "100%" }}>
                    <div className={classes.title}>
                        <Typography variant="h6" id="tableTitle">
                            Bounty Packs
                        </Typography>
                    </div>
                    <div className={classes.actions}>
                        <div style={{ marginLeft: 15, display: "inline-block" }}>
                            <NewPack
                                buttonText="New Pack"
                                refreshParent={this.refreshList}
                            />
                        </div>
                    </div>
                </div>

                <div style={{ float: "left", width: "100%" }}>
                    {this.formatTableBody(this.state.packs)}
                </div>
            </Paper>
        </div>;
    }
}

const styles: any = theme => ({
    table: {
        minWidth: 700,
    },
    title: {
        flex: "0 0 auto",
        float: "left",
    },
    actions: {
        color: theme.palette.text.secondary,
        float: "right",
    },
});

export const BountyPacks = withStyles(styles)(BountyPacksWrapped);
